
/**
 * @fileoverview Vue Number Input Directive
 * 
 * This module provides a Vue.js directive that enforces numeric input formatting
 * with configurable modes for both integer and decimal inputs. The directive
 * supports real-time validation, cursor preservation, and v-model synchronization.
 * 
 * Supported modes:
 * - Integer mode: Only digits (0-9) allowed
 * - Decimal mode: Digits with up to 2 decimal places, comma-to-period conversion
 * 
 * @module v-number
 * @requires vue
 * @author RhomTrip Development Team
 */

/**
 * Normalizes a value into a decimal string with up to two fractional digits
 * 
 * This utility function formats input values to a consistent decimal format
 * while preserving string nature for intermediate typing states.
 * 
 * @function normalizeDecimalInput
 * @param {string|number|null|undefined} value - The raw input value to format
 * @returns {string} A formatted decimal string with up to 2 decimal places, or empty string for falsy input
 * 
 * @description
 * Processing behavior:
 * - Converts falsy values to empty string
 * - Converts commas to periods for international keyboard support
 * - Removes all non-numeric characters except periods
 * - Keeps only the first decimal separator (removes subsequent periods)
 * - Truncates fractional part to maximum 2 digits
 * - Preserves string format to allow typing intermediate states
 * 
 * @example
 * normalizeDecimalInput('12,34')     // Returns '12.34'
 * normalizeDecimalInput('12.345')    // Returns '12.34'
 * normalizeDecimalInput('12..34')    // Returns '12.34'
 * normalizeDecimalInput('12abc.34')  // Returns '12.34'
 * normalizeDecimalInput('12.')       // Returns '12.'
 * normalizeDecimalInput('')          // Returns ''
 */
function normalizeDecimalInput(value) {
    if (!value) return '';
    value = String(value);

    // Convert commas to periods for international support
    value = value.replace(/,/g, '.');

    // Remove all non-numeric characters except periods
    value = value.replace(/[^0-9.]/g, '')

    // Handle multiple periods - keep only the first one
    const parts = value.split('.');
    if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('').replace(/\./g, '');
    }

    // Limit decimal places to maximum 2 digits
    const [integer, decimals] = value.split('.');
    if (decimals !== undefined) {
        value = `${integer}.${decimals.slice(0, 2)}`;
    }
    return value;
}

/**
 * Normalizes a value to contain only integer digits
 * 
 * Removes all non-digit characters from the input value, ensuring only
 * numeric digits (0-9) remain in the resulting string.
 * 
 * @function normalizeIntegerInput
 * @param {string|number|null|undefined} value - The input value to normalize
 * @returns {string} A string containing only numeric digits, or empty string if input is falsy
 * 
 * @description
 * This function performs the following operations:
 * - Returns empty string for falsy values
 * - Converts input to string representation
 * - Removes all characters except digits (0-9) using regex pattern
 * - Preserves the order of remaining numeric characters
 * 
 * @example
 * normalizeIntegerInput('123abc456')  // Returns '123456'
 * normalizeIntegerInput('abc')        // Returns ''
 * normalizeIntegerInput('12.34')      // Returns '1234'
 * normalizeIntegerInput('')           // Returns ''
 * normalizeIntegerInput(null)         // Returns ''
 * normalizeIntegerInput(123)          // Returns '123'
 */
function normalizeIntegerInput(value) {
    if (!value) return '';
    const strValue = String(value);

    // Remove any character that is not a digit (0-9)
    return strValue.replace(/[^\d]/g, '');
}

/**
 * Vue Configurable Number Input Directive
 * 
 * A flexible Vue directive that enforces numeric input formatting with configurable
 * modes for integer-only or decimal inputs. Provides real-time validation while
 * maintaining cursor position and v-model synchronization.
 * 
 * @namespace v-number
 * @type {Object}
 * @property {Function} bind - Vue directive bind hook that sets up numeric input filtering
 * 
 * @description
 * Features:
 * - Configurable modes: 'integer' or 'decimal'
 * - Real-time input filtering and validation
 * - Preserves cursor position during formatting
 * - Maintains v-model synchronization
 * - Performance optimized (only updates when changes occur)
 * - International support (comma-to-period conversion in decimal mode)
 * - Graceful error handling for cursor positioning
 * 
 * Mode Behaviors:
 * - **integer**: Only allows digits 0-9, removes all other characters
 * - **decimal**: Allows digits and one decimal point, up to 2 decimal places
 * 
 * @example
 * // Integer mode usage:
 * <input v-number="'integer'" v-model="quantity" placeholder="Enter quantity">
 * <input v-number="'integer'" type="text" @input="handleQuantityChange">
 * 
 * @example
 * // Decimal mode usage:
 * <input v-number="'decimal'" v-model="price" placeholder="Enter price">
 * <input v-number="'decimal'" type="text" @input="handlePriceChange">
 * 
 * @example
 * // Register globally:
 * Vue.directive('number', numberDirective)
 * 
 * @example
 * // Alternative usage with default mode (decimal):
 * <input v-number v-model="count">
 * <input v-number="'integer'" v-model="count">

 * 
 * @important
 * **Input Type Considerations:**
 * - Recommended to use with `type="text"` for best compatibility
 * - Using with `type="number"` may cause conflicts in some browsers
 * - The directive handles validation, so `type="number"` is often unnecessary
 * - For mobile keyboards, consider using `inputmode="numeric"` or `inputmode="decimal"`
 * 
 * @example
 * // Recommended usage patterns:
 * <input v-number="'integer'" type="text" inputmode="numeric" v-model="quantity">
 * <input v-number="'decimal'" type="text" inputmode="decimal" v-model="price">
 * 
 * // Avoid these patterns (may cause browser conflicts):
 * <input v-number type="number" v-model="value">  // Not recommended
 */
export default {

    /**
     * Vue directive bind hook
     * 
     * Called when the directive is first bound to the element. Sets up an input
     * event listener that applies numeric formatting based on the configured mode
     * while preserving cursor position and maintaining v-model synchronization.
     * 
     * @param {HTMLInputElement} el - The input element the directive is bound to
     * @param {Object} binding - Vue directive binding object containing the mode value
     * @param {string|undefined} binding.value - The formatting mode: 'integer', 'decimal', or undefined (defaults to 'integer')
     * @param {Object} vnode - The virtual node produced by Vue's compiler
     * @returns {void}
     * 
     * @description
     * Mode Configuration:
     * - When binding.value is 'decimal': Uses decimal formatting (up to 2 decimal places)
     * - When binding.value is 'integer' or undefined: Uses integer formatting (digits only)
     * - Default behavior: Falls back to integer mode if no valid mode specified
     * 
     * Event Handler Lifecycle:
     * 1. Listens for 'input' events on the target element
     * 2. Applies appropriate normalization based on configured mode
     * 3. Updates element value only if formatting resulted in changes
     * 4. Preserves cursor position for seamless user experience
     * 5. Dispatches new 'input' event to maintain v-model synchronization
     * 6. Gracefully handles cursor positioning errors
     * 
     * Performance Optimization:
     * - Only triggers updates when formatting actually changes the value
     * - Prevents unnecessary event dispatching and re-rendering cycles
     * - Minimizes DOM manipulation by checking value changes first
     * 
     * Browser Compatibility:
     * - Handles cursor positioning gracefully across different browsers
     * - Silent fallback for input types that don't support selection range
     * - Compatible with all modern browsers and IE11+
     * - Note: Some browsers may have conflicts with type="number" and cursor positioning
     * 
     * Input Type Compatibility:
     * - Works best with type="text" 
     * - May have cursor positioning issues with type="number" in some browsers
     * - Consider using inputmode attribute for mobile keyboard optimization
     * 
     * @example
     * // Recommended input configurations:
     * <input v-number="'integer'" type="text" inputmode="numeric">
     * <input v-number="'decimal'" type="text" inputmode="decimal">
     * 
     * @example
     * // Integer mode transformations:
     * // "123abc456" → "123456" (removes non-digits)
     * // "12.34" → "1234" (removes decimal point)
     * // "abc" → "" (removes all non-digits)
     * // "+123" → "123" (removes plus sign)
     * // "-456" → "456" (removes minus sign)
     * 
     * @example
     * // Decimal mode transformations:
     * // "12,34" → "12.34" (comma conversion)
     * // "12.345" → "12.34" (decimal truncation)
     * // "12abc.34" → "12.34" (character filtering)
     * // "12..34" → "12.34" (multiple periods handling)
     * // ".5" → ".5" (preserves leading decimal)
     * 
     * @see {@link normalizeIntegerInput} for integer formatting logic
     * @see {@link normalizeDecimalInput} for decimal formatting logic
     */
    bind(el, binding, vnode) {
        const mode = binding.value; // Expected values: 'decimal', 'integer', or undefined (defaults to integer)

        /**
         * Debug logging for directive binding
         * 
         * @description Logs the mode configuration when directive is bound to element.
         * Useful for debugging directive behavior and mode configuration.
         * Remove or comment out in production builds for performance.
         * 
         * @param {string|undefined} mode - The configured mode for the directive
         */

        /**
         * Input event handler function
         * 
         * Handles real-time numeric formatting of input values based on the configured
         * mode while preserving cursor position and maintaining Vue reactivity.
         * 
         * @param {InputEvent} event - The native DOM input event
         * @returns {void}
         * 
         * @description
         * Processing steps:
         * 1. Extract current input value from event target
         * 2. Apply appropriate normalization based on mode configuration
         * 3. Compare formatted value with original to detect changes
         * 4. Update input value and cursor position if changes occurred
         * 5. Dispatch synthetic input event for v-model synchronization
         * 
         * Mode Selection Logic:
         * - 'decimal': Uses normalizeDecimalInput for decimal formatting
         * - 'integer', undefined, or any other value: Uses normalizeIntegerInput for integer formatting
         * - This ensures safe fallback behavior for undefined or invalid modes
         * 
         * Cursor Position Management:
         * - Saves cursor position before value modification
         * - Restores cursor position after value update
         * - Maintains typing flow and user experience
         * - Handles edge cases where cursor positioning might fail
         * 
         * Event Synchronization:
         * - Dispatches synthetic 'input' event after value changes
         * - Ensures Vue's reactivity system detects the change
         * - Maintains compatibility with v-model directive
         * - Triggers computed properties and watchers as expected
         * 
         * Error Handling:
         * - Cursor positioning wrapped in try-catch for browser compatibility
         * - Silent fallback for input types that don't support selection range
         * - Graceful degradation in case of unexpected errors
         * - Special handling for type="number" inputs where setSelectionRange may fail
         * 
         * Performance Considerations:
         * - Only processes input when actual formatting changes occur
         * - Avoids unnecessary DOM manipulation and event dispatching
         * - Minimal overhead for already-formatted input values
         */
        el.addEventListener("input", (event) => {
            const input = event.target;
            const formatted = mode === 'decimal' ? normalizeDecimalInput(input.value) : normalizeIntegerInput(input.value);

            // Only update and trigger event if the value has changed (something was filtered)
            if (formatted !== input.value) {
                // Save cursor position before updating value
                const cursorPos = input.selectionStart;

                // Update input value with filtered content
                input.value = formatted;

                // Restore cursor position to maintain user experience
                try {
                    input.setSelectionRange(cursorPos, cursorPos);
                } catch (e) {
                    // Silent fallback - cursor positioning may fail in some edge cases
                    // Common scenarios:
                    // - input[type="number"] in some browsers doesn't support setSelectionRange
                    // - Hidden or non-focusable elements
                    // - Some mobile browsers with virtual keyboards
                    // This is expected behavior and doesn't affect functionality
                }

                // Dispatch input event to synchronize v-model and trigger reactive updates
                input.dispatchEvent(new Event('input'));
            }
        });
    },
};